import { useGetOrderRatingsQuery } from 'api/orderRatings'
import { OrderRating, OrderRatingSortDirection } from 'api/orderRatings.types'
import { FeedbackTableRow } from 'components/FeedbackTable'
import { DEFAULT_PAGE_SIZE, PageSize } from 'components/TablePaginationFooter'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

// Matches src/scenes/RecoveryAI/Activity's search debounce so the two feedback
// surfaces feel identical.
const SEARCH_DEBOUNCE_MS = 300

// Route base for the conversation-less rating detail page. Lives under
// /order_feedback (not /recovery_ai) because every RAI route redirects home
// for a merchant without the recovery-ai flag.
export const RATING_DETAIL_PATH = '/order_feedback/ratings'

// One OrderRating view-model row → W0's shared FeedbackTableRow. A pure rename
// (+ storeName→locationName); resolvedBy passes straight through because the wire
// shape is identical to ResolvedByValue. Every nullable field survives as null,
// so the shared table renders its em-dash placeholders. Exported for a unit test.
export const toFeedbackTableRow = (rating: OrderRating): FeedbackTableRow => ({
  id: rating.id,
  rating: rating.rating,
  date: rating.date,
  customerName: rating.customerName,
  locationName: rating.storeName,
  resolvedBy: rating.resolvedBy,
  conversationId: rating.conversationId,
  comment: rating.comment,
  // Handed to the rating detail page in router state — it keys the customer
  // and recent-ratings panels off it without a lookup of its own.
  userId: rating.userId,
  userUid: rating.userUid,
})

// Every row is viewable. A conversation-backed rating opens the RecoveryAI case
// detail (which owns the transcript and the operator actions); a rating with no
// conversation — non-RAI, operator-dismissed, or unresolved — opens the Reports
// rating detail page, which shows the same layout with the customer's comment in
// place of the transcript. Exported for a unit test.
//
// Typed on the two fields it actually reads rather than on FeedbackTableRow, so
// the detail page's Recent-activities panel (whose rows are OrderRatings, not
// table rows) shares this ONE branch instead of re-inlining it. Still assignable
// to FeedbackTable's `getViewPath: (row: FeedbackTableRow) => string | null`
// prop — a function requiring fewer fields accepts everything that prop passes.
export const getViewPath = (row: {
  id: string
  conversationId: string | null
}): string =>
  row.conversationId
    ? `/recovery_ai/cases/${row.conversationId}`
    : `${RATING_DETAIL_PATH}/${row.id}`

// Owns all Details-pane state + the order_ratings query + row normalization.
// hideResolved seeds UNCHECKED here (satisfaction-report framing) — W3's
// Escalations tab seeds the SAME component CHECKED (queue framing). This
// per-surface default is a product decision (master-plan §7 Q1), NOT a bug; a
// reviewer comparing the two surfaces will otherwise file one of them.
export const useOrderRatings = () => {
  const [searchInput, setSearchInput] = useState('') // raw box value
  const [search, setSearch] = useState('') // debounced term sent to the API
  const [hideResolved, setHideResolved] = useState(false)
  const [locationId, setLocationId] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE)
  const [sortDirection, setSortDirection] =
    useState<OrderRatingSortDirection>('desc')

  const { data, isFetching, isError, refetch } = useGetOrderRatingsQuery({
    page,
    per_page: pageSize,
    search: search || undefined,
    location_id: locationId ?? undefined,
    hide_resolved: hideResolved,
    sort: 'date',
    sort_direction: sortDirection,
  })

  // Every filter/sort change also snaps back to page 1 (a filtered set is
  // shorter, so the current page number may no longer exist). Done imperatively
  // in each handler — the Activity idiom — not via an effect on the params.
  const [debouncedSetSearch, cancelDebouncedSearch] = useDebouncedCallback(
    (value: string) => {
      setSearch(value)
      setPage(1)
    },
    SEARCH_DEBOUNCE_MS
  )
  // Drop any in-flight debounced update on unmount so setState never fires on an
  // unmounted tree.
  useEffect(() => cancelDebouncedSearch, [cancelDebouncedSearch])

  const onSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value)
      debouncedSetSearch(value)
    },
    [debouncedSetSearch]
  )
  const onHideResolvedChange = useCallback((value: boolean) => {
    setHideResolved(value)
    setPage(1)
  }, [])
  const onLocationChange = useCallback((id: number | null) => {
    setLocationId(id)
    setPage(1)
  }, [])
  const onSortChange = useCallback((direction: 'asc' | 'desc') => {
    setSortDirection(direction)
    setPage(1)
  }, [])
  const onPageChange = useCallback((next: number) => setPage(next), [])
  const onPageSizeChange = useCallback((size: PageSize) => {
    setPageSize(size)
    setPage(1)
  }, [])
  const onRetry = useCallback(() => void refetch(), [refetch])

  const rows = useMemo<FeedbackTableRow[]>(
    () => (data?.data ?? []).map(toFeedbackTableRow),
    [data]
  )

  const hasActiveFilters = search !== '' || hideResolved || locationId !== null

  return {
    // toolbar
    searchValue: searchInput,
    onSearchChange,
    hideResolved,
    onHideResolvedChange,
    locationId,
    onLocationChange,
    // table
    rows,
    isLoading: isFetching,
    isError,
    onRetry,
    sortDirection,
    onSortChange,
    hasActiveFilters,
    // footer
    page,
    pageSize,
    totalCount: data?.meta.totalCount ?? 0,
    totalPages: data?.meta.totalPages ?? 0,
    onPageChange,
    onPageSizeChange,
  }
}
